"use client";

import "flag-icons/css/flag-icons.min.css";
import { Spinner, useBreakpointValue } from "@chakra-ui/react";
import React, { memo, useMemo } from "react";
import {
  AiOutlineClear,
  AiOutlineLogin,
  AiOutlineLogout,
  AiOutlineReload,
  AiOutlineWechatWork,
} from "react-icons/ai";
import { BiSortUp } from "react-icons/bi";
import { BsInfoCircle, BsSignStop, BsThreeDotsVertical } from "react-icons/bs";
import { CiEdit, CiLock, CiUnlock } from "react-icons/ci";
import { CgAssign, CgProfile, CgReorder } from "react-icons/cg";
import {
  FaAngry,
  FaArrowRight,
  FaFrown,
  FaGrinHearts,
  FaMeh,
  FaRegCompass,
  FaSmile,
  FaTelegram,
  FaWpexplorer,
} from "react-icons/fa";
import { FaToggleOff, FaToggleOn } from "react-icons/fa6";
import { FiSend, FiShare2 } from "react-icons/fi";
import {
  // GiCrystalBars,
  GiHamburgerMenu,
  GiLoad,
  GiAwareness,
  GiCrystalBars,
} from "react-icons/gi";
import { GoDownload, GoPersonAdd, GoSearch, GoUpload } from "react-icons/go";
import { GrSelect, GrUpdate } from "react-icons/gr";
import {
  HiAdjustments,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  // HiOutlinePrinter,
} from "react-icons/hi";
import { IoIosAddCircleOutline, IoIosHelpCircleOutline } from "react-icons/io";
import { IoCopyOutline, IoWarningOutline } from "react-icons/io5";
import {
  LuBriefcase,
  LuBuilding,
  LuCheck,
  LuChevronDown,
  LuFileText,
  LuFolderOpen,
  LuGraduationCap,
  LuMinus,
  LuMoon,
  LuPlus,
  LuSun,
  // LuUserCog,
} from "react-icons/lu";
import {
  MdClose,
  MdDashboardCustomize,
  MdDragIndicator,
  MdErrorOutline,
  MdEventNote,
  MdExpandMore,
  MdFiberManualRecord,
  MdInsights,
  MdKeyboardDoubleArrowDown,
  MdKeyboardVoice,
  MdOutlineClear,
  MdOutlineDeselect,
  MdOutlineOpenWith,
  MdOutlineRateReview,
  MdSettings,
} from "react-icons/md";
import { PiSealCheck, PiSelection, PiTreeStructureThin } from "react-icons/pi";
import {
  RiAiGenerate2,
  RiDeleteBinLine,
  RiSave3Fill,
  RiUserSettingsLine,
} from "react-icons/ri";
import { SiStagetimer, SiWeightsandbiases, SiWritedotas } from "react-icons/si";
import {
  TbError404,
  TbHomeDown,
  TbHomeUp,
  TbFileTextSpark,
  TbSettingsSearch,
  TbTargetArrow,
  TbSettingsShare,
} from "react-icons/tb";
import { TfiThought } from "react-icons/tfi";
import { TiDeleteOutline } from "react-icons/ti";
import { VscEmptyWindow, VscFeedback, VscOpenPreview } from "react-icons/vsc";
import { IoIosReturnLeft } from "react-icons/io";
// import { GiFloatingCrystal } from "react-icons/gi";

type GenericIconConfig =
  | {
      kind: "icon";
      name: string;
      icon: React.ElementType;
    }
  | {
      kind: "flag";
      name: string;
      countryCode: string;
    };

export type GenericIconSize = "xs" | "sm" | "md" | "lg";

export type ResponsiveIconSize =
  | GenericIconSize
  | Partial<
      Record<"base" | "sm" | "md" | "lg" | "xl" | "2xl", GenericIconSize>
    >;

export const chakraToReactSizeMapping = Object.freeze({
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
} satisfies Record<GenericIconSize, number>);

export const GENERIC_ICON_CONFIG = {
  color_mode_light: { kind: "icon", name: "Light mode", icon: LuSun },
  color_mode_dark: { kind: "icon", name: "Dark mode", icon: LuMoon },
  update: { kind: "icon", name: "Update", icon: GrUpdate },
  open: { kind: "icon", name: "Open", icon: MdOutlineOpenWith },
  confirm: { kind: "icon", name: "Confirm", icon: LuCheck },
  confirmed_thought: {
    kind: "icon",
    name: "Confirmed thought",
    icon: PiSealCheck,
  },
  suggested_thought_review: {
    kind: "icon",
    name: "Suggested thought review",
    icon: MdOutlineRateReview,
  },
  edit: { kind: "icon", name: "Edit", icon: CiEdit },
  record: { kind: "icon", name: "Record", icon: MdFiberManualRecord },
  add: { kind: "icon", name: "Add", icon: IoIosAddCircleOutline },
  delete_soft: { kind: "icon", name: "Delete", icon: TiDeleteOutline },
  delete_permanent: {
    kind: "icon",
    name: "Delete permanently",
    icon: RiDeleteBinLine,
  },
  close: { kind: "icon", name: "Close", icon: MdClose },
  shape: { kind: "icon", name: "Shape", icon: GiCrystalBars },
  // shape: { kind: "icon", name: "Shape", icon: GiFloatingCrystal },
  extract: { kind: "icon", name: "Shape from source", icon: TbFileTextSpark },
  insight: { kind: "icon", name: "Insight", icon: MdInsights },
  insight_bias: {
    kind: "icon",
    name: "Insight bias",
    icon: SiWeightsandbiases,
  },
  download: { kind: "icon", name: "Download", icon: GoDownload },
  upload: { kind: "icon", name: "Import", icon: GoUpload },
  search: { kind: "icon", name: "Search", icon: GoSearch },
  search_settings: {
    kind: "icon",
    name: "Search settings",
    icon: TbSettingsSearch,
  },
  microphone: {
    kind: "icon",
    name: "Microphone",
    icon: MdKeyboardVoice,
  },
  adjust: { kind: "icon", name: "Adjust", icon: HiAdjustments },
  chat: { kind: "icon", name: "Chat", icon: AiOutlineWechatWork },
  select: { kind: "icon", name: "Select", icon: PiSelection },
  click_to_select: {
    kind: "icon",
    name: "Click to select",
    icon: GrSelect,
  },
  unselect: { kind: "icon", name: "Unselect", icon: MdOutlineDeselect },
  reset: { kind: "icon", name: "Reset", icon: AiOutlineClear },
  send: { kind: "icon", name: "Send", icon: FiSend },
  help: { kind: "icon", name: "Help", icon: IoIosHelpCircleOutline },
  reload: { kind: "icon", name: "Reload", icon: AiOutlineReload },
  replay: { kind: "icon", name: "Replay", icon: AiOutlineReload },
  clear: { kind: "icon", name: "Clear selection", icon: MdOutlineClear },
  share: { kind: "icon", name: "Share", icon: FiShare2 },
  collect_feedback: {
    kind: "icon",
    name: "Feedback request",
    icon: VscFeedback,
  },
  scroll_down: {
    kind: "icon",
    name: "Scroll down",
    icon: MdKeyboardDoubleArrowDown,
  },
  preview: { kind: "icon", name: "Preview", icon: VscOpenPreview },
  drag_and_drop: {
    kind: "icon",
    name: "Drag and drop",
    icon: MdDragIndicator,
  },
  explore: { kind: "icon", name: "Explore", icon: FaWpexplorer },
  assign: { kind: "icon", name: "Assign", icon: CgAssign },
  to_left: {
    kind: "icon",
    name: "Move left",
    icon: HiOutlineChevronDoubleLeft,
  },
  to_right: {
    kind: "icon",
    name: "Move right",
    icon: HiOutlineChevronDoubleRight,
  },
  copy_clipboard: {
    kind: "icon",
    name: "Copy to clipboard",
    icon: IoCopyOutline,
  },
  identify_topics: {
    kind: "icon",
    name: "Identify topics",
    icon: PiTreeStructureThin,
  },
  generate: { kind: "icon", name: "Generate", icon: RiAiGenerate2 },
  write: { kind: "icon", name: "Write", icon: SiWritedotas },
  transcribe: { kind: "icon", name: "Transcribe", icon: SiWritedotas },
  personalize: {
    kind: "icon",
    name: "Personalize",
    // icon: GoPersonAdd,
    icon: RiUserSettingsLine,
  },
  inform: { kind: "icon", name: "Inform", icon: BsInfoCircle },
  plus: { kind: "icon", name: "Plus", icon: LuPlus },
  minus: { kind: "icon", name: "Minus", icon: LuMinus },
  lock: { kind: "icon", name: "Lock", icon: CiLock },
  unlock: { kind: "icon", name: "Unlock", icon: CiUnlock },
  toggle_on: { kind: "icon", name: "Toggle on", icon: FaToggleOn },
  toggle_off: { kind: "icon", name: "Toggle off", icon: FaToggleOff },
  wait: { kind: "icon", name: "Wait", icon: SiStagetimer },
  log_out: { kind: "icon", name: "Log out", icon: AiOutlineLogout },
  log_in: { kind: "icon", name: "Log in", icon: AiOutlineLogin },
  reflect_thoughts: {
    kind: "icon",
    name: "Reflect thoughts",
    icon: TfiThought,
  },
  likert_strongly_disagree: {
    kind: "icon",
    name: "Strongly disagree",
    icon: FaAngry,
  },
  likert_disagree: {
    kind: "icon",
    name: "Disagree",
    icon: FaFrown,
  },
  likert_neutral: {
    kind: "icon",
    name: "Neutral",
    icon: FaMeh,
  },
  likert_agree: {
    kind: "icon",
    name: "Agree",
    icon: FaSmile,
  },
  likert_strongly_agree: {
    kind: "icon",
    name: "Strongly agree",
    icon: FaGrinHearts,
  },
  rerank: { kind: "icon", name: "Order by focus lens", icon: BiSortUp },
  expand_more: { kind: "icon", name: "Expand more", icon: MdExpandMore },
  reassign: { kind: "icon", name: "Reassign", icon: CgReorder },
  anchor: { kind: "icon", name: "Anchor to situation", icon: CgAssign },
  save: { kind: "icon", name: "Save", icon: RiSave3Fill },
  profile_version: {
    kind: "icon",
    name: "Profile version",
    icon: RiSave3Fill,
  },
  load: { kind: "icon", name: "Load", icon: GiLoad },
  target: { kind: "icon", name: "Target", icon: TbTargetArrow },
  profile_focus: { kind: "icon", name: "Focus lens", icon: TbTargetArrow },
  profile: { kind: "icon", name: "Profile", icon: CgProfile },
  studio: {
    kind: "icon",
    name: "Studio",
    icon: MdDashboardCustomize,
  },
  situation: { kind: "icon", name: "Situation", icon: MdEventNote },
  personal: { kind: "icon", name: "Personal", icon: GoPersonAdd },
  settings: { kind: "icon", name: "Settings", icon: MdSettings },
  settings_print: {
    kind: "icon",
    name: "Output settings",
    // icon: HiOutlinePrinter,
    // icon: TbDatabaseExport,
    icon: TbSettingsShare,
  },
  info: { kind: "icon", name: "Info", icon: BsInfoCircle },
  three_dots_vertical: {
    kind: "icon",
    name: "More actions",
    icon: BsThreeDotsVertical,
  },
  main_actions: {
    kind: "icon",
    name: "Main actions",
    icon: MdDashboardCustomize,
  },
  burger_menu: {
    kind: "icon",
    name: "Menu",
    icon: GiHamburgerMenu,
  },
  empty: { kind: "icon", name: "Empty", icon: VscEmptyWindow },
  compass: { kind: "icon", name: "Compass", icon: FaRegCompass },
  // trace_origin: {
  //   kind: "icon",
  //   name: "Trace source material",
  //   icon: FaRegCompass,
  // },
  origin_source: { kind: "icon", name: "Source material", icon: LuFileText },
  backlog: { kind: "icon", name: "Backlog", icon: LuFolderOpen },
  adaptive_memory: {
    kind: "icon",
    name: "Adaptive memory",
    icon: RiAiGenerate2,
  },
  error: { kind: "icon", name: "Error", icon: MdErrorOutline },
  telegram: { kind: "icon", name: "Telegram", icon: FaTelegram },
  stop: { kind: "icon", name: "Stop", icon: BsSignStop },
  warning: { kind: "icon", name: "Warning", icon: IoWarningOutline },
  not_found: { kind: "icon", name: "Not found", icon: TbError404 },
  arrow_right: { kind: "icon", name: "Arrow right", icon: FaArrowRight },
  slash_forward: {
    kind: "icon",
    name: "Slash forward",
    icon: LuChevronDown,
  },
  home_up: { kind: "icon", name: "Home up", icon: TbHomeUp },
  home_down: { kind: "icon", name: "Home down", icon: TbHomeDown },
  en: { kind: "flag", name: "English", countryCode: "gb" },
  de: { kind: "flag", name: "German", countryCode: "de" },
  sk: { kind: "flag", name: "Slovak", countryCode: "sk" },
  job_activity: {
    kind: "icon",
    name: "Job activity",
    icon: LuBriefcase,
  },
  learning_outcome: {
    kind: "icon",
    name: "Learning outcome",
    icon: LuGraduationCap,
  },
  workplace_characteristic: {
    kind: "icon",
    name: "Workplace characteristic",
    icon: LuBuilding,
  },
  individual_description: {
    kind: "icon",
    name: "Individual description",
    // icon: LuUserCog,
    icon: GiAwareness,
  },
  return: { kind: "icon", name: "Return", icon: IoIosReturnLeft },
} as const satisfies Record<string, GenericIconConfig>;

export type GenericIconType = keyof typeof GENERIC_ICON_CONFIG;

export function normalizeIconSize(size: ResponsiveIconSize): GenericIconSize {
  if (typeof size === "string") return size;
  return size.base || "md";
}

export type GenericIconProps = {
  icon: GenericIconType;
  size?: ResponsiveIconSize;
  color?: string;
  visuallyInactive?: boolean;
  highlighted?: boolean;
  highlightedColor?: string;
  humanReadable?: string;
  loading?: boolean;
};

function GenericIcon({
  icon,
  size,
  color,
  visuallyInactive = false,
  highlighted = false,
  highlightedColor = "#7e22ce",
  humanReadable,
  loading = false,
}: GenericIconProps): JSX.Element | null {
  const sizeValue =
    typeof size === "string"
      ? ({ base: size } as const)
      : size || { base: undefined };

  const resolvedSize = useBreakpointValue(sizeValue) as
    | GenericIconSize
    | undefined;

  const reactIconSize = useMemo(() => {
    if (!resolvedSize) return undefined;
    return chakraToReactSizeMapping[resolvedSize];
  }, [resolvedSize]);

  if (loading) {
    return <Spinner size={size} />;
  }

  const style: React.CSSProperties = {};
  if (visuallyInactive) style.opacity = 0.5;
  if (highlighted) {
    style.opacity = 0.7;
    style.filter = `drop-shadow(0 0 2px ${highlightedColor})`;
  }

  const iconConfig = GENERIC_ICON_CONFIG[icon];
  const accessibleName = humanReadable ?? iconConfig.name;

  if (iconConfig.kind === "flag") {
    return (
      <span
        className={`fi fi-${iconConfig.countryCode}`}
        style={{
          fontSize: reactIconSize ? `${reactIconSize}px` : undefined,
          lineHeight: 1,
          ...style,
        }}
        role="img"
        aria-label={accessibleName}
        title={accessibleName}
      />
    );
  }

  if (iconConfig.kind !== "icon") {
    return null;
  }

  const IconComponent = iconConfig.icon;

  return (
    <IconComponent
      title={accessibleName}
      aria-label={accessibleName}
      size={reactIconSize}
      color={color}
      style={style}
    />
  );
}

export default memo(GenericIcon);
